import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { toast } from 'sonner'

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { EmptyState, Page, PageHeader } from '@/components/shell'
import { api, ApiError } from '@/lib/api'
import { useCan } from '@/lib/session'

export const Route = createFileRoute('/dashboard/agents')({
  component: AgentsPage,
})

type AspBinding = { tokenAddress: string; agentId: number; creator: string }
type BindingLookup = {
  binding: AspBinding | null
  buyback: { enabled: boolean; readable: boolean } | null
}
type UnbindResult = { agentId: number; tokenAddress: string; removed: number; audited: true }

/** 输入既可以是 agentId(纯数字)也可以是 token 地址(0x…),据此选参数。 */
function parseQuery(raw: string): { agentId?: number; token?: string } | null {
  const value = raw.trim()
  if (!value) return null
  if (/^0x[0-9a-fA-F]{40}$/.test(value)) return { token: value.toLowerCase() }
  if (/^\d+$/.test(value)) {
    const n = Number(value)
    return Number.isSafeInteger(n) && n > 0 ? { agentId: n } : null
  }
  return null
}

function AgentsPage() {
  const can = useCan()
  const qc = useQueryClient()
  const [input, setInput] = useState('')
  const [submitted, setSubmitted] = useState<{ agentId?: number; token?: string } | null>(null)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [reason, setReason] = useState('')

  const query = useQuery({
    queryKey: ['agent-binding', submitted],
    enabled: submitted !== null,
    queryFn: () =>
      api.get<BindingLookup>('/agents/binding', submitted ?? undefined),
    retry: false,
  })

  const unbind = useMutation({
    mutationFn: (input: { agentId?: number; tokenAddress?: string; reason: string }) =>
      api.post<UnbindResult>('/agents/unbind', input),
    onSuccess: (result) => {
      toast.success('已解绑并写入审计', {
        description: `agent ${result.agentId} ↔ ${result.tokenAddress}（删除 ${result.removed} 条）`,
      })
      setConfirmOpen(false)
      setReason('')
      qc.invalidateQueries({ queryKey: ['agent-binding'] })
    },
    onError: (error) => {
      toast.error('解绑失败', {
        description: error instanceof ApiError ? error.message : '请稍后重试',
      })
    },
  })

  const submit = () => {
    const parsed = parseQuery(input)
    if (!parsed) {
      toast.error('请输入正确的 agentId(数字)或代币地址(0x…)')
      return
    }
    setSubmitted(parsed)
  }

  const data = query.data
  const binding = data?.binding ?? null
  const buyback = data?.buyback ?? null
  const trimmedReason = reason.trim()
  const reasonValid = trimmedReason.length >= 10 && trimmedReason.length <= 200
  // 有活跃回购或回购状态读不到时,一律不允许后台解绑(与 ignix 侧守卫同口径)。
  const buybackBlocks = Boolean(buyback && (buyback.enabled || !buyback.readable))
  const canUnbind = can('agent', 'unbind') && !!binding && !buybackBlocks

  return (
    <Page>
      <PageHeader
        title="Agent 管理"
        description="查询并解绑「代币 → Agent」关联。用于项目方被狙击、无法自行签名解绑时,由平台侧发起。"
      />

      <div className="surface rounded-apple-lg p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && submit()}
            placeholder="输入 agentId(如 11211)或代币地址(0x…)"
            className="font-mono"
          />
          <Button onClick={submit} disabled={!input.trim()} className="shrink-0">
            查询关联
          </Button>
        </div>
      </div>

      {submitted !== null && (
        <div className="surface rounded-apple-lg p-4">
          {query.isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-5 w-48" />
              <Skeleton className="h-5 w-72" />
            </div>
          ) : query.isError ? (
            <EmptyState
              title="查询失败"
              description={
                query.error instanceof ApiError ? query.error.message : '请稍后重试'
              }
            />
          ) : !binding ? (
            <EmptyState title="没有找到关联" description="这个 agentId / 代币当前没有绑定关系。" />
          ) : (
            <div className="space-y-4">
              <dl className="grid gap-3 sm:grid-cols-2">
                <Field label="Agent ID">
                  <span className="font-mono">{binding.agentId}</span>
                </Field>
                <Field label="回购托管">
                  {!buyback ? (
                    <span className="text-mut">—</span>
                  ) : !buyback.readable ? (
                    <Badge variant="outline" className="text-warn">状态读取失败</Badge>
                  ) : buyback.enabled ? (
                    <Badge variant="outline" className="text-warn">已开通(不可解绑)</Badge>
                  ) : (
                    <Badge variant="outline">未开通</Badge>
                  )}
                </Field>
                <Field label="代币地址">
                  <span className="font-mono break-all">{binding.tokenAddress}</span>
                </Field>
                <Field label="关联时发射者">
                  <span className="font-mono break-all">{binding.creator}</span>
                </Field>
              </dl>

              {buybackBlocks && (
                <p className="text-sm text-warn">
                  {buyback?.enabled
                    ? '该代币已开通回购托管,链上 Controller 仍引用此 agent,不能在后台解绑;请先让 ASP 撤销回购授权。'
                    : '回购状态无法核验,出于安全不允许解绑;请确认 RPC 后重试。'}
                </p>
              )}

              <div className="flex justify-end">
                <Button
                  variant="destructive"
                  disabled={!canUnbind}
                  onClick={() => setConfirmOpen(true)}
                >
                  解绑
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={(open) => !unbind.isPending && setConfirmOpen(open)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>确认解绑</AlertDialogTitle>
            <AlertDialogDescription>
              将删除 agent {binding?.agentId} 与代币 {binding?.tokenAddress} 的关联。此操作会即时在主站生效并写入审计。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <label className="text-sm text-mut">解绑理由(10–200 字,写入审计)</label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="例如:项目方被狙击,应其请求由平台侧解除 agent 关联"
              rows={3}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={unbind.isPending}>取消</AlertDialogCancel>
            <Button
              variant="destructive"
              disabled={!reasonValid || unbind.isPending || !binding}
              onClick={() =>
                binding &&
                unbind.mutate({ agentId: binding.agentId, reason: trimmedReason })
              }
            >
              {unbind.isPending ? '解绑中…' : '确认解绑'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Page>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-mut">{label}</dt>
      <dd className="mt-1">{children}</dd>
    </div>
  )
}
